/**
 * Helpers genéricos de string, número e objeto que substituem funções do lodash
 * por implementações nativas. Cada helper replica a semântica documentada do
 * lodash via JSDoc; divergências relevantes estão sinalizadas.
 */

// ----------------------------------------------------------------------------
// Strings
// ----------------------------------------------------------------------------

/**
 * Converte o primeiro caractere para maiúsculo e o restante para minúsculo,
 * substituindo o `capitalize` do lodash por implementação nativa.
 *
 * Assim como no lodash, o restante da string é minúsculado (ex.: `'FOO BAR'`
 * vira `'Foo bar'`) e valores `null`/`undefined`/`''` retornam `''`.
 */
export function capitalize(value?: string | null): string {
  if (!value) return ''
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase()
}

/**
 * Converte apenas o primeiro caractere para maiúsculo, mantendo o restante
 * inalterado, substituindo o `upperFirst` do lodash por implementação nativa.
 *
 * Valores `null`/`undefined`/`''` retornam `''`.
 */
export function upperFirst(value?: string | null): string {
  if (!value) return ''
  return value.charAt(0).toUpperCase() + value.slice(1)
}

/**
 * Remove os diacríticos (acentos) de uma string, substituindo o `deburr` do
 * lodash por implementação nativa via API `String.prototype.normalize`.
 *
 * Decompõe os caracteres na forma `NFD` e remove as marcas combinantes
 * (`U+0300`–`U+036F`). Valores `null`/`undefined` retornam `''`.
 *
 * Diferente do `deburr` do lodash, apenas marcas combinantes são removidas:
 * caracteres que não decompõem em NFD (`ß`, `æ`, `ø`, `ł`, `đ`) são mantidos
 * como estão. Suficiente para pt-BR, onde `ç`, `ã`, `é`, `ô` etc. decompõem
 * normalmente.
 */
export function deburr(value?: string | null): string {
  return (value ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '')
}

// ----------------------------------------------------------------------------
// Números
// ----------------------------------------------------------------------------

/**
 * Arredonda `value` para o número de casas decimais informado em `precision`
 * (padrão `0`), substituindo o `round` do lodash por implementação nativa.
 *
 * Usa notação exponencial para mitigar erros de ponto flutuante (ex.: `1.005`),
 * replicando o comportamento do `_.round` — `Math.round` puro só arredonda para
 * inteiro e perderia a precisão informada.
 */
export function round(value: number, precision: number = 0): number {
  const [mantissa, exponent = '0'] = `${value}e`.split('e')
  const rounded = Math.round(Number(`${mantissa}e${Number(exponent) + precision}`))
  const [resultMantissa, resultExponent = '0'] = `${rounded}e`.split('e')
  return Number(`${resultMantissa}e${Number(resultExponent) - precision}`)
}

/**
 * Restringe `value` ao intervalo fechado `[lower, upper]`, substituindo o
 * `clamp` do lodash por implementação nativa.
 */
export function clamp(value: number, lower: number, upper: number): number {
  return Math.min(Math.max(value, lower), upper)
}

/**
 * Verifica se `value` está no intervalo semiaberto `[start, end)`, substituindo
 * o `inRange` do lodash por implementação nativa.
 *
 * Quando `end` é omitido, o intervalo é `[0, start)`. Se `start` for maior que
 * `end`, os limites são invertidos — mesmo comportamento do `_.inRange`.
 */
export function inRange(value: number, start: number, end?: number): boolean {
  const [lower, upper] = end === undefined ? [0, start] : [start, end]
  return value >= Math.min(lower, upper) && value < Math.max(lower, upper)
}

/**
 * Converte `value` para inteiro na base `radix`, substituindo o `parseInt` do
 * lodash por implementação nativa.
 *
 * Converte `value` para string e remove espaços nas pontas antes de delegar ao
 * `Number.parseInt` nativo. Quando `radix` não é informado, a base é
 * auto-detectada (`10` por padrão, `16` para o prefixo `'0x'`/`'0X'`) — mesmo
 * comportamento do `_.parseInt`.
 */
export function parseInteger(value: string | number, radix?: number): number {
  return Number.parseInt(String(value).trim(), radix)
}

// ----------------------------------------------------------------------------
// Objetos
// ----------------------------------------------------------------------------

/**
 * Cria um objeto com as mesmas chaves de `object`, aplicando `iteratee` a cada
 * valor, substituindo o `mapValues` do lodash por implementação nativa.
 *
 * Aceita `null`/`undefined`, retornando `{}`.
 *
 * @param object Objeto de origem
 * @param iteratee Função aplicada a cada valor; recebe `(valor, chave)`
 */
export function mapValues<T extends object, R>(
  object: T | null | undefined,
  iteratee: (value: T[keyof T], key: string) => R
): { [K in keyof T]: R } {
  return Object.fromEntries(
    Object.entries(object ?? {}).map(([key, value]) => [key, iteratee(value as T[keyof T], key)])
  ) as { [K in keyof T]: R }
}

/**
 * Cria um objeto com os mesmos valores de `collection`, recalculando cada chave
 * com `iteratee`, substituindo o `mapKeys` do lodash por implementação nativa.
 *
 * Aceita arrays (as chaves originais passadas ao `iteratee` são os índices).
 *
 * @param collection Objeto ou array de origem
 * @param iteratee Função que retorna a nova chave; recebe `(valor, chave)`
 */
export function mapKeys<V>(
  collection: { [key: string]: V } | ArrayLike<V> | null | undefined,
  iteratee: (value: V, key: string) => PropertyKey
): Record<string, V> {
  return Object.fromEntries(
    Object.entries(collection ?? {}).map(([key, value]) => [iteratee(value, key), value])
  ) as Record<string, V>
}

/**
 * Retorna um novo objeto contendo apenas as `keys` informadas que existem em
 * `object`, substituindo o `pick` do lodash por implementação nativa.
 *
 * Diferente do `pick` do lodash (e do `omit` deste módulo), aceita apenas
 * chaves planas — caminhos aninhados no formato `'a.b'` não são suportados.
 * Aceita `null`/`undefined`, retornando `{}`.
 */
export function pick<T extends object>(object: T | null | undefined, keys: ReadonlyArray<PropertyKey>): Partial<T> {
  if (object == null) return {}
  const wanted = new Set(keys.map(String))
  return Object.fromEntries(Object.entries(object).filter(([key]) => wanted.has(key))) as Partial<T>
}

/**
 * Retorna um novo objeto contendo apenas as propriedades em que `predicate`
 * retorna verdadeiro, substituindo o `pickBy` do lodash por implementação nativa.
 *
 * O predicado recebe `(valor, chave)`. Aceita `null`/`undefined`, retornando `{}`.
 */
export function pickBy<T extends object>(
  object: T | null | undefined,
  predicate: (value: T[keyof T], key: string) => boolean
): Partial<T> {
  if (object == null) return {}
  return Object.fromEntries(
    Object.entries(object).filter(([key, value]) => predicate(value as T[keyof T], key))
  ) as Partial<T>
}

/**
 * Retorna um novo objeto sem as `paths` informadas, substituindo o `omit` do
 * lodash por implementação nativa.
 *
 * Aceita uma chave única ou uma lista de chaves e suporta caminhos aninhados no
 * formato `'a.b'` (remove apenas a folha, clonando o caminho para não mutar o
 * objeto original) — mesmo comportamento do `_.omit`. Assim como o lodash, um
 * caminho que existe como chave literal no objeto (ex.: `'a.b'` em
 * `{ 'a.b': 1 }`) é removido diretamente, sem ser interpretado como aninhado.
 * Aceita `null`/`undefined`, retornando `{}`.
 */
export function omit<T extends object>(
  object: T | null | undefined,
  paths: PropertyKey | ReadonlyArray<PropertyKey>
): Partial<T> {
  if (object == null) return {}
  const pathList = (Array.isArray(paths) ? paths : [paths]).map(String)
  const result = { ...object } as Record<string, unknown>

  for (const path of pathList) {
    if (path.includes('.') && !(path in result)) {
      removePath(result, path.split('.'))
    } else {
      delete result[path]
    }
  }

  return result as Partial<T>
}

function removePath(target: Record<string, unknown>, segments: string[]): void {
  const [head, ...rest] = segments

  if (rest.length === 0) {
    delete target[head]
    return
  }

  const next = target[head]
  if (next != null && typeof next === 'object') {
    const clone = (Array.isArray(next) ? [...next] : { ...next }) as Record<string, unknown>
    target[head] = clone
    removePath(clone, rest)
  }
}

/**
 * Retorna um novo objeto removendo as propriedades em que `predicate` retorna
 * verdadeiro, substituindo o `omitBy` do lodash por implementação nativa.
 *
 * O predicado recebe `(valor, chave)`. Aceita `null`/`undefined`, retornando `{}`.
 */
export function omitBy<T extends object>(
  object: T | null | undefined,
  predicate: (value: T[keyof T], key: string) => boolean
): Partial<T> {
  if (object == null) return {}
  return Object.fromEntries(
    Object.entries(object).filter(([key, value]) => !predicate(value as T[keyof T], key))
  ) as Partial<T>
}

/**
 * Indexa os itens de `collection` por uma chave, substituindo o `keyBy` do
 * lodash por implementação nativa.
 *
 * A `iteratee` pode ser uma função que recebe o item e retorna a chave, ou o
 * nome de uma propriedade do item. Quando há chaves repetidas, prevalece o
 * último item — mesmo comportamento do `_.keyBy`. Aceita `null`/`undefined`,
 * retornando `{}`.
 */
export function keyBy<T>(
  collection: ArrayLike<T> | null | undefined,
  iteratee: ((item: T) => PropertyKey) | keyof T
): Record<string, T> {
  const getKey = typeof iteratee === 'function' ? iteratee : (item: T) => item[iteratee]
  const result: Record<string, T> = {}

  for (const item of Array.from(collection ?? [])) {
    result[String(getKey(item))] = item
  }

  return result
}

/**
 * Agrupa os itens de `collection` por uma chave, substituindo o `groupBy` do
 * lodash por implementação nativa.
 *
 * A `iteratee` pode ser uma função que recebe o item e retorna a chave, ou o
 * nome de uma propriedade do item — assim como no `_.groupBy`, apenas chaves
 * planas são suportadas dessa forma; caminhos aninhados no formato `'a.b'`
 * exigem uma função iteratee (ex.: `(item) => item.a.b`). Preserva a ordem
 * original dos itens dentro de cada grupo. Aceita `null`/`undefined`,
 * retornando `{}`.
 */
export function groupBy<T>(
  collection: ArrayLike<T> | null | undefined,
  iteratee: ((item: T) => PropertyKey) | keyof T
): Record<string, T[]> {
  const result: Record<string, T[]> = {}

  for (const item of Array.from(collection ?? [])) {
    const key = String(typeof iteratee === 'function' ? iteratee(item) : item[iteratee])
    const group = result[key] ?? []
    group.push(item)
    result[key] = group
  }

  return result
}

/**
 * Verifica se `object` possui `key` como propriedade própria, substituindo o
 * `has` do lodash por implementação nativa.
 *
 * Diferente do `_.has`, aceita apenas chaves planas — caminhos aninhados no
 * formato `'a.b'` não são suportados. Aceita `null`/`undefined`, retornando `false`.
 */
export function has(object: unknown, key: PropertyKey): boolean {
  return object != null && Object.hasOwn(Object(object), key)
}

/**
 * Itera sobre as propriedades de `object`, acumulando o resultado em `result`
 * através de `iteratee`, substituindo o `transform` do lodash por implementação
 * nativa.
 *
 * Quando `accumulator` não é informado, `result` começa como `{}` — mesmo
 * valor inicial usado pelo `_.transform` quando `object` é um objeto plano. A
 * iteração pode ser interrompida antecipadamente retornando `false` na
 * `iteratee` — mesmo comportamento do `_.transform`.
 */
export function transform<T extends object, R extends object = Record<string, unknown>>(
  object: T | null | undefined,
  iteratee: (result: R, value: T[keyof T], key: keyof T) => unknown,
  accumulator?: R
): R {
  const result = accumulator ?? ({} as R)

  for (const [key, value] of Object.entries(object ?? {})) {
    if (iteratee(result, value as T[keyof T], key as keyof T) === false) break
  }

  return result
}
